/**
 * The opt-in context of a survey that hangs off a beta feature.
 *
 * **Three different things, not five tiles.** Laid out as one row of equal boxes, the reader has to
 * work out the arithmetic that is the whole point of the block. So the block says it instead:
 *
 * - **the progression** — of the 120 who ever turned the feature on, 95 still have it and 25 went
 *   back. They are not three islands; 95 + 25 = 120, and the split is drawn as one bar because
 *   that is what the numbers are;
 * - **the revert rate** is derived from two of those (25 ÷ 120), so it hangs off the 25 instead of
 *   claiming a sixth fact's worth of attention;
 * - **who answered and then left** is not about the feature at all — it is the feature crossed with
 *   *this survey*, which is why it sits apart, under its own heading. Those are the people whose
 *   answers explain the revert rate, and they are invisible in the questionnaire's own results.
 *
 * Zero gets its own words. "Ninguém que respondeu desligou depois" is a finding; a bare `0` in a
 * box reads as missing data.
 *
 * The block is simply absent when the survey has no opt-in condition: the payload omits `optIn`,
 * and inventing zeros here would claim a feature nobody attached.
 *
 * **Why the feature stops there.** The block used to end with "motivos de saída mais escolhidos",
 * read off a `reason` column that no longer exists (BFF 5.0.0). Why somebody left is now a survey
 * answer like any other, so in an exit questionnaire it is already above, aggregated per question,
 * in the words the people actually read — and repeating it here would be a second, worse copy.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';
import type { SurveyResultsOptIn } from '../../../Types/Surveys.types';
import { formatCount, formatPercent } from '../../../Utils/surveyFormat';
import { SegmentedBar } from '@/Shared/components/SegmentedBar/SegmentedBar';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

const headStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const nameStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const keyStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/** The three numbers of the progression live in one box, because they are one sentence. */
const groupStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  max-width: 760px;
`;

const totalStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-xs);
`;

const totalValueStyle = css`
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const totalLabelStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const splitStyle = css`
  display: flex;
  gap: var(--space-ml);
  flex-wrap: wrap;
`;

const partStyle = css`
  display: flex;
  align-items: baseline;
  gap: 5px;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const partValueStyle = css`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--color-text);
`;

const swatchStyle = css`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  align-self: center;
`;

/** The rate is derived from the two numbers beside it, so it whispers rather than announces. */
const derivedStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const crossingStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding-top: var(--space-m);
  border-top: 1px solid var(--color-divider);
  max-width: 760px;
`;

const crossingTitleStyle = css`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
`;

const crossingValueStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-xs);
`;

const crossingNumberStyle = css`
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--color-danger);
`;

/** Zero here is a result, not a gap, so it is written rather than printed. */
const crossingNoneStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-text);
`;

const crossingHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

export interface ResultsOptInBlockProps {
  optIn: SurveyResultsOptIn;
  id?: string;
}

export const ResultsOptInBlock = ({ optIn, id }: ResultsOptInBlockProps): JSX.Element => (
  <div id={id} className={wrapStyle}>
    <div className={headStyle}>
      <span className={nameStyle}>{optIn.featureName}</span>
      <span className={keyStyle}>{optIn.featureKey}</span>
    </div>

    <div className={groupStyle}>
      <span className={totalStyle}>
        <span className={totalValueStyle}>{formatCount(optIn.everEnabled)}</span>
        <span className={totalLabelStyle}>já ligaram a funcionalidade alguma vez</span>
      </span>

      <SegmentedBar
        segments={[
          { label: 'Continuam com ela', value: optIn.enabledNow, color: 'var(--color-success)' },
          { label: 'Voltaram atrás', value: optIn.reverted, color: 'var(--color-danger)' },
        ]}
        label={`${optIn.enabledNow} continuam, ${optIn.reverted} voltaram atrás`}
        emptyText='ninguém ligou ainda'
      />

      <div className={splitStyle}>
        <span className={partStyle}>
          <span className={swatchStyle} style={{ background: 'var(--color-success)' }} />
          <span className={partValueStyle}>{formatCount(optIn.enabledNow)}</span>
          continuam
        </span>
        <span className={partStyle}>
          <span className={swatchStyle} style={{ background: 'var(--color-danger)' }} />
          <span className={partValueStyle}>{formatCount(optIn.reverted)}</span>
          voltaram atrás
          <span className={derivedStyle}>· {formatPercent(optIn.revertRate)} de quem ligou</span>
        </span>
      </div>
    </div>

    <div className={crossingStyle}>
      <span className={crossingTitleStyle}>Cruzando com esta pesquisa</span>

      {optIn.respondentsWhoReverted > 0 ? (
        <span className={crossingValueStyle}>
          <span className={crossingNumberStyle}>{formatCount(optIn.respondentsWhoReverted)}</span>
          <span className={crossingHintStyle}>
            pessoas responderam e depois desligaram a funcionalidade
          </span>
        </span>
      ) : (
        <span className={crossingNoneStyle}>
          Ninguém que respondeu desligou a funcionalidade depois.
        </span>
      )}

      <span className={crossingHintStyle}>
        São as pessoas cujas respostas explicam a taxa de arrependimento — e que não aparecem em
        nenhum outro número desta tela.
      </span>
    </div>
  </div>
);
